import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Text,
  Heading,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import t from "../translations";

const MotionBox = motion(Box);

const fadeInUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Hero({ color }) {
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const particleColor = useColorModeValue("#805AD5", "#B794F4");
  const lineColor = useColorModeValue("#805AD5", "#B794F4");
  const [engineReady, setEngineReady] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setEngineReady(true));
  }, []);

  const particleOptions = useMemo(
    () => ({
      fullScreen: false,
      particles: {
        number: { value: 40, density: { enable: true, area: 900 } },
        color: { value: particleColor },
        links: {
          enable: true,
          color: lineColor,
          opacity: 0.15,
          distance: 120,
        },
        move: { enable: true, speed: 0.6, outModes: "bounce" },
        size: { value: { min: 1, max: 2.5 } },
        opacity: { value: { min: 0.15, max: 0.4 } },
      },
      interactivity: {
        events: {
          onHover: { enable: false },
          onClick: { enable: false },
        },
      },
      detectRetina: true,
    }),
    [particleColor, lineColor]
  );

  return (
    <Box id="hero" position="relative" overflow="clip">

      {engineReady && (
        <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={0} pointerEvents="none" style={{ touchAction: "none" }}>
          <Particles
            id="hero-particles"
            options={particleOptions}
          />
        </Box>
      )}
      <Container maxW="4xl" position="relative" zIndex={1}>
        <Stack
          as={Box}
          textAlign="center"
          spacing={{ base: 4, md: 6 }}
          pt={{ base: 20, md: 24 }}
          pb={{ base: 10, md: 16 }}
        >
        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <Text fontSize={{ base: "lg", md: "xl" }} color={subtitleColor}>
            {t.hero.greeting}
          </Text>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <Heading
            fontWeight={800}
            fontSize={{ base: "3xl", sm: "4xl", md: "6xl" }}
            lineHeight="shorter"
            className="gradient-text"
          >
            {t.hero.name}
          </Heading>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <Heading
            fontWeight={600}
            fontSize={{ base: "lg", sm: "xl", md: "3xl" }}
            color={`${color}.400`}
            minH={{ base: "28px", sm: "36px", md: "48px" }}
          >
            <TypeAnimation
              sequence={t.hero.roles.flatMap((role) => [role, 2500])}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              style={{ color: "inherit" }}
            />
          </Heading>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <Text
            color={subtitleColor}
            fontSize={{ base: "md", sm: "lg", md: "xl" }}
            maxW="2xl"
            mx="auto"
            lineHeight="tall"
          >
            {t.hero.description}
          </Text>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={4}
        >
          <MotionBox
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={{ base: 3, sm: 4 }}
              justify="center"
              align="center"
              pt={4}
            >
              {[
                { label: t.hero.primaryButton, target: "experience" },
                { label: t.hero.secondaryButton, target: "contact", variant: "outline" },
              ].map(({ label, target, variant }) => (
                <Button
                  key={target}
                  variant={variant}
                  colorScheme={color}
                  size={{ base: "md", md: "lg" }}
                  rounded="full"
                  px={{ base: 6, md: 8 }}
                  w={{ base: "full", sm: "auto" }}
                  onClick={() =>
                    document
                      .getElementById(target)
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </MotionBox>
        </MotionBox>
        </Stack>
      </Container>
    </Box>
  );
}
